import { CREATE_ENTITY_CARD_CUSTOM_TYPE } from "constants/chatType";
import { SUBMITTED } from "constants/sensitiveData";
import { useCurrentChatGroupContext } from "hooks/useCurrentChatGroupContext";
import { useToast } from "hooks/useToast";
import { useChannelStateContext } from "stream-chat-react";
import { useSubmitAIQuestionCardMutation } from "store/apis/aiQuestionCard";
import { useGetCountriesDataQuery } from "store/apis/onboarding";
import { BackendError } from "types/utils/error";
import { OnboardingEntityDraft } from "v2/components/onboarding/OnboardingDataContext";
import { AddEntityForm } from "v2/components/onboarding/steps/ConfirmEntityStep/AddEntityForm";
import {
  useAddGroupEntityMutation,
  useUpdateOnboardingEntityMutation,
} from "v2/store/onboardingEntityApi";
import { joinLegalName } from "v2/utils/joinLegalName";
import { toEntityInput } from "v2/utils/toEntityInput";
import { LLC_TAX_OPTIONS } from "v2/components/onboarding/steps/EntityTypeStep/entityOptions";
import { formatDate } from "utils/formatDate";
import { Divider } from "components/design/Divider";
import { CrossCircle } from "components/icons/CrossCircle";

type CreatedEntityMetadata = {
  parent_entity_id?: string;
  parentName?: string;
  name?: string;
  uuid?: string;
  type?: string;
  taxedAs?: string;
  dateOfIncorporation?: string;
  address?: string;
  created?: boolean;
};

type CreateEntityCardProps = {
  customData: {
    header?: string;
    metadata?: CreatedEntityMetadata;
  };
  messageId: string;
  groupId: string;
  cardStatus: string;
};

const EMPTY_RELATION_OPTIONS: { value: string; label: string }[] = [];

const DetailRow = ({ label, value }: { label: string; value?: string }) => {
  if (!value) return null;

  return (
    <div className="t-flex t-justify-between t-gap-2">
      <span className="t-text-body-sm t-text-text-30">{label}</span>
      <span className="t-text-body-sm t-text-text-100">{value}</span>
    </div>
  );
};

const CreatedEntityDetails = ({
  metadata,
}: {
  metadata: CreatedEntityMetadata;
}) => {
  const taxedAs = LLC_TAX_OPTIONS.find(
    (option) => option.key === metadata.taxedAs
  );
  return (
    <div className="t-flex t-flex-col t-gap-3 t-rounded-lg t-p-3 t-shadow-light-30">
      <DetailRow label="Entity name" value={metadata.name} />
      <Divider />
      <DetailRow label="Entity type" value={metadata.type} />
      <Divider />
      {taxedAs?.name && (
        <>
          <DetailRow label="Taxed as" value={taxedAs?.name} />
          <Divider />
        </>
      )}
      {metadata.dateOfIncorporation && (
        <>
          <DetailRow
            label="Date of incorporation"
            value={formatDate(metadata.dateOfIncorporation)}
          />
          <Divider />
        </>
      )}
      {metadata.address && (
        <>
          <DetailRow label="Address" value={metadata.address} />
          <Divider />
        </>
      )}
      <DetailRow label="Linked to" value={metadata.parentName} />
    </div>
  );
};

const CancelledEntityNotice = () => (
  <div className="t-flex t-items-center t-gap-2 t-rounded-lg t-bg-red-0 t-p-3 t-shadow-light-30">
    <span className="t-text-red">
      <CrossCircle />
    </span>
    <span className="t-text-body-sm t-text-text-60">
      Entity creation cancelled
    </span>
  </div>
);

const buildAddressString = (draft: OnboardingEntityDraft): string =>
  draft.addressData
    ? [
        draft.addressData.street_address,
        draft.addressData.city,
        draft.addressData.state,
        draft.addressData.zipcode,
      ]
        .filter(Boolean)
        .join(", ")
    : "";

const buildEntitySummaryMessage = (
  legalName: string,
  draft: OnboardingEntityDraft,
  address: string,
  parentName?: string
): string =>
  [
    "New entity created:",
    `Name: ${legalName}`,
    `Entity type: ${draft.entityTypeLabel}`,
    draft.taxedAs && `Taxed as: ${draft.taxedAs}`,
    draft.dateOfIncorporation &&
      `Date of incorporation: ${draft.dateOfIncorporation}`,
    address && `Address: ${address}`,
    parentName && `Linked to: ${parentName}`,
  ]
    .filter(Boolean)
    .join("\n");

export const CreateEntityCard = ({
  customData,
  messageId,
  groupId,
  cardStatus,
}: CreateEntityCardProps) => {
  const { channel } = useChannelStateContext();
  const { alertToast } = useToast();
  const { entities } = useCurrentChatGroupContext();
  const { data: countries } = useGetCountriesDataQuery();
  const [addGroupEntity] = useAddGroupEntityMutation();
  const [updateOnboardingEntity] = useUpdateOnboardingEntityMutation();
  const [submitAIQuestionCard] = useSubmitAIQuestionCardMutation();

  const metadata = customData.metadata;
  const parentEntityId = metadata?.parent_entity_id;
  const parentEntityName = entities.find(
    (entity) => entity.uuid === parentEntityId
  )?.name;
  const isSubmitted = cardStatus === SUBMITTED;

  const handleCancel = async () => {
    try {
      await submitAIQuestionCard({
        groupId,
        messageId,
        customType: CREATE_ENTITY_CARD_CUSTOM_TYPE,
        metadata: { created: false },
      }).unwrap();
      await channel.sendMessage({
        text: "Entity creation cancelled.",
      });
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  const handleSave = async (draft: OnboardingEntityDraft) => {
    const legalName = joinLegalName(draft.entityName, draft.entitySuffix);
    const address = buildAddressString(draft);
    const input = toEntityInput(draft);

    try {
      const added = await addGroupEntity({ groupId, input }).unwrap();

      if (parentEntityId) {
        await updateOnboardingEntity({
          entityId: added.entityId,
          input,
          relation: { parentId: parentEntityId, childId: added.entityId },
        }).unwrap();
      }

      const createdMetadata: CreatedEntityMetadata = {
        created: true,
        uuid: added.entityId,
        name: legalName,
        type: draft.entityTypeLabel,
        taxedAs: draft.taxedAs,
        dateOfIncorporation: draft.dateOfIncorporation,
        address,
        parentName: parentEntityName,
        parent_entity_id: parentEntityId,
      };

      await submitAIQuestionCard({
        groupId,
        messageId,
        customType: CREATE_ENTITY_CARD_CUSTOM_TYPE,
        metadata: createdMetadata,
      }).unwrap();

      await channel.sendMessage({
        text: buildEntitySummaryMessage(
          legalName,
          draft,
          address,
          parentEntityName
        ),
        custom_data: {
          metadata: createdMetadata,
        },
      });
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  if (isSubmitted && metadata?.created === false) {
    return <CancelledEntityNotice />;
  }

  if (isSubmitted && metadata?.uuid) {
    return <CreatedEntityDetails metadata={metadata} />;
  }

  return (
    <AddEntityForm
      mode="separate"
      relationOptions={EMPTY_RELATION_OPTIONS}
      countries={countries}
      showStateField
      requireStateField
      showDateField
      singleColumn
      onSave={handleSave}
      onCancel={handleCancel}
    />
  );
};
